import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { UseSearchBoxProps } from 'react-instantsearch';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';

import { Input } from 'components/@tedui';
import {
  IconPositionType,
  InputProps
} from 'components/@tedui/Atoms/Input/Input.props';

import { useTrackClickEvent } from '../../analytics';
import { debounce, updateUrlWithParams } from '../../utils';

const queryHook: UseSearchBoxProps['queryHook'] = (query, search) => {
  search(query);
};

export function CustomSearchBox({
  filterOnClick
}: Pick<InputProps, 'filterOnClick'>): React.ReactNode {
  const router = useRouter();
  const ISTALKSPAGE = router.pathname === '/talks';

  const { indexUiState, results } = useInstantSearch();
  const [query, setQuery] = useState('');
  const [searchCount, setSearchCount] = useState(0);
  const searchTimeRef = useRef(Date.now());

  const { refine } = useSearchBox({
    queryHook
  });

  const trackClick = useTrackClickEvent(
    results
      ? {
          index: results.index,
          nbHits: results.nbHits
        }
      : undefined
  );

  useEffect(() => {
    const { query: queryFromIndexState } = indexUiState;
    setQuery(queryFromIndexState || '');
  }, [indexUiState]);

  const onClick = useCallback(() => {
    const now = Date.now();
    const timePassed = now - searchTimeRef.current;
    const rateLimitTimeFrame = 60000;
    const maxSearchesPerMinute = 15;

    const clickEvent = () => {
      updateUrlWithParams(router, {
        q: query
      });
    };

    if (
      timePassed < rateLimitTimeFrame &&
      searchCount >= maxSearchesPerMinute
    ) {
      console.warn("You've exceeded the maximum search rate.");
      return;
    }

    if (timePassed >= rateLimitTimeFrame) {
      setSearchCount(0);
      searchTimeRef.current = now;
    }

    setSearchCount(prevCount => prevCount + 1);

    trackClick({
      eventName: 'talk_index_search',
      additionalEvents: {
        search_term: query
      },
      waitForResultsVolume: true
    });

    // If we're on the talks page, we need to refine the query after the router is finished pushing
    if (!ISTALKSPAGE) {
      Promise.resolve(clickEvent()).then(() => {
        refine(query);
      });
    } else {
      clickEvent();
    }
  }, [
    ISTALKSPAGE,
    indexUiState.sortBy,
    query,
    refine,
    router,
    searchCount,
    trackClick
  ]);

  const debouncedClickAction = debounce(onClick, 500);

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = event.currentTarget.value;
    setQuery(newQuery);
  }, []);

  return (
    <Input
      id="search"
      label="Search for a talk"
      type="search"
      value={query}
      onChange={onChange}
      searchOnClick={debouncedClickAction}
      iconOptions={{
        name: 'search',
        position: IconPositionType.Leading
      }}
      filterOnClick={filterOnClick}
    />
  );
}
